import React, { useState, useEffect, useContext } from 'react';
import { toast } from 'react-toastify';
import { Line, Bar, Pie } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import AuthContext from '../../context/AuthContext';
import AdminService from '../../services/adminService';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
);

const AdminStats = () => {
  const { token } = useContext(AuthContext);
  const [loading, setLoading] = useState(true);
  const [timeframe, setTimeframe] = useState('monthly');
  const [userStats, setUserStats] = useState({
    labels: [],
    datasets: []
  });
  const [workoutStats, setWorkoutStats] = useState({
    labels: [],
    datasets: []
  });
  const [categoryStats, setCategoryStats] = useState({
    labels: [],
    datasets: []
  });
  const [platformStats, setPlatformStats] = useState({
    totalUsers: 0,
    activeUsers: 0,
    totalWorkouts: 0,
    totalExercises: 0,
    avgWorkoutsPerUser: 0,
    avgWorkoutDuration: 0
  });

  useEffect(() => {
    fetchAllStats();
  }, [token, timeframe]);

  const fetchAllStats = async () => {
    try {
      setLoading(true);
      
      // Get overview stats using AdminService
      const overviewData = await AdminService.getStatsOverview(token);
      
      if (overviewData.success) {
        setPlatformStats({
          totalUsers: overviewData.stats.totalUsers || 0,
          activeUsers: overviewData.stats.activeUsers || 0,
          totalWorkouts: overviewData.stats.totalWorkouts || 0,
          totalExercises: overviewData.stats.totalExercises || 0,
          avgWorkoutsPerUser: overviewData.stats.avgWorkoutsPerUser || 0,
          avgWorkoutDuration: overviewData.stats.avgWorkoutDuration || 0
        });
      }
      
      // Get user growth data using AdminService
      const userData = await AdminService.getUserStats(timeframe, token);
      
      if (userData.success) {
        setUserStats({
          labels: userData.labels || [],
          datasets: [
            {
              label: 'New Users',
              data: userData.newUsers || [],
              fill: false,
              backgroundColor: 'rgba(75, 192, 192, 0.2)',
              borderColor: 'rgba(75, 192, 192, 1)',
              tension: 0.1
            },
            {
              label: 'Active Users',
              data: userData.activeUsers || [],
              fill: false,
              backgroundColor: 'rgba(54, 162, 235, 0.2)',
              borderColor: 'rgba(54, 162, 235, 1)',
              tension: 0.1
            }
          ]
        });
      }
      
      // Get workout activity data using AdminService
      const workoutData = await AdminService.getWorkoutStats(timeframe, token);
      
      if (workoutData.success) {
        setWorkoutStats({
          labels: workoutData.labels || [],
          datasets: [
            {
              label: 'Workouts Completed',
              data: workoutData.counts || [],
              backgroundColor: 'rgba(153, 102, 255, 0.6)',
              borderColor: 'rgba(153, 102, 255, 1)',
              borderWidth: 1
            }
          ]
        });
      }
      
      // Get category usage using AdminService
      const categoryData = await AdminService.getCategoryStats(token);
      
      if (categoryData.success) {
        // Generate colors for each category
        const backgroundColors = [
          'rgba(255, 99, 132, 0.6)',
          'rgba(54, 162, 235, 0.6)',
          'rgba(255, 206, 86, 0.6)',
          'rgba(75, 192, 192, 0.6)',
          'rgba(153, 102, 255, 0.6)',
          'rgba(255, 159, 64, 0.6)',
          'rgba(199, 199, 199, 0.6)',
          'rgba(83, 102, 255, 0.6)',
          'rgba(78, 205, 196, 0.6)',
          'rgba(255, 99, 71, 0.6)'
        ];
        
        setCategoryStats({
          labels: categoryData.categories || [],
          datasets: [
            {
              label: 'Exercise Categories',
              data: categoryData.counts || [],
              backgroundColor: backgroundColors.slice(0, categoryData.categories.length),
              borderWidth: 1
            }
          ]
        });
      }
      
      setLoading(false);
    } catch (error) {
      console.error('Error fetching stats:', error);
      toast.error('Failed to load statistics');
      setLoading(false);
    }
  };

  const handleTimeframeChange = (e) => {
    setTimeframe(e.target.value);
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
      },
      tooltip: {
        mode: 'index',
        intersect: false,
      }
    },
    scales: {
      y: {
        beginAtZero: true
      }
    }
  };

  const pieOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'right',
      }
    }
  };

  if (loading && (!userStats.labels.length || !workoutStats.labels.length)) {
    return <div className="loading">Loading statistics...</div>;
  }

  return (
    <div className="admin-stats">
      <h1>Platform Statistics</h1>
      
      <div className="stats-controls">
        <div className="form-group">
          <label htmlFor="timeframe">Timeframe:</label>
          <select
            id="timeframe"
            value={timeframe}
            onChange={handleTimeframeChange}
          >
            <option value="weekly">Weekly</option>
            <option value="monthly">Monthly</option>
            <option value="yearly">Yearly</option>
          </select>
        </div>
      </div>
      
      {/* Platform Overview */}
      <div className="stats-overview">
        <h2>Platform Overview</h2>
        <div className="grid-3">
          <div className="stat-card">
            <h3>{platformStats.totalUsers}</h3>
            <p>Total Users</p>
          </div>
          <div className="stat-card">
            <h3>{platformStats.activeUsers}</h3>
            <p>Active Users</p>
          </div>
          <div className="stat-card">
            <h3>{platformStats.totalWorkouts}</h3>
            <p>Total Workouts</p>
          </div>
          <div className="stat-card">
            <h3>{platformStats.totalExercises}</h3>
            <p>Total Exercises</p>
          </div>
          <div className="stat-card">
            <h3>{platformStats.avgWorkoutsPerUser.toFixed(1)}</h3>
            <p>Avg Workouts/User</p>
          </div>
          <div className="stat-card">
            <h3>{platformStats.avgWorkoutDuration.toFixed(0)} min</h3>
            <p>Avg Workout Duration</p>
          </div>
        </div>
      </div>
      
      {/* User Growth Chart */}
      <div className="chart-container">
        <h2>User Growth</h2>
        <div className="chart-wrapper">
          {userStats.labels.length > 0 ? (
            <Line data={userStats} options={chartOptions} height={300} />
          ) : (
            <p className="no-data">No user data available for the selected timeframe.</p>
          )}
        </div>
      </div>
      
      {/* Workout Frequency Chart */}
      <div className="chart-container">
        <h2>Workout Activity</h2>
        <div className="chart-wrapper">
          {workoutStats.labels.length > 0 ? (
            <Bar data={workoutStats} options={chartOptions} height={300} />
          ) : (
            <p className="no-data">No workout data available for the selected timeframe.</p>
          )}
        </div>
      </div>
      
      {/* Exercise Categories Chart */}
      <div className="chart-container">
        <h2>Exercise Categories</h2>
        <div className="chart-wrapper">
          {categoryStats.labels.length > 0 ? (
            <Pie data={categoryStats} options={pieOptions} height={300} />
          ) : (
            <p className="no-data">No category data available.</p>
          )}
        </div>
      </div>
      
      <div className="export-section">
        <h2>Export Data</h2>
        <div className="export-buttons">
          <button className="btn btn-primary">
            Export User Data
          </button>
          <button className="btn btn-secondary">
            Export Workout Data
          </button>
          <button className="btn btn-dark">
            Export Full Report
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdminStats;
